import { useAuth } from "@/_core/hooks/useAuth";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import DashboardLayout from "@/components/DashboardLayout";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { useCompany } from "@/contexts/CompanyContext";
import { useState } from "react";
import {
  TrendingUp,
  Clock,
  DollarSign,
  BarChart3,
  PieChart,
  Award,
} from "lucide-react";

export default function TemplateAnalytics() {
  const { isAuthenticated } = useAuth();
  const { selectedCompanyId } = useCompany();
  const [period, setPeriod] = useState<"week" | "month" | "quarter" | "year">(
    "month"
  );

  // Fetch analytics data
  const { data: overallStats, isLoading: statsLoading } =
    trpc.templateAnalytics.getOverallStats.useQuery(
      { companyId: selectedCompanyId || 1 },
      { enabled: isAuthenticated && !!selectedCompanyId }
    );

  const { data: usageTrends, isLoading: trendsLoading } =
    trpc.templateAnalytics.getUsageTrends.useQuery(
      { companyId: selectedCompanyId || 1, period },
      { enabled: isAuthenticated && !!selectedCompanyId }
    );

  const { data: costSavings, isLoading: savingsLoading } =
    trpc.templateAnalytics.getCostSavings.useQuery(
      { companyId: selectedCompanyId || 1, period },
      { enabled: isAuthenticated && !!selectedCompanyId }
    );

  const { data: rankings, isLoading: rankingsLoading } =
    trpc.templateAnalytics.getTemplateRankings.useQuery(
      { companyId: selectedCompanyId || 1, limit: 5 },
      { enabled: isAuthenticated && !!selectedCompanyId }
    );

  const { data: categoryBreakdown, isLoading: breakdownLoading } =
    trpc.templateAnalytics.getCategoryBreakdown.useQuery(
      { companyId: selectedCompanyId || 1, period },
      { enabled: isAuthenticated && !!selectedCompanyId }
    );

  const isLoading =
    statsLoading ||
    trendsLoading ||
    savingsLoading ||
    rankingsLoading ||
    breakdownLoading;

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="container py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-1/3"></div>
            <div className="grid md:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-32 bg-muted rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container py-8">
        <div className="space-y-8">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">Template Analytics</h1>
              <p className="text-muted-foreground mt-2">
                Usage trends, cost savings, and insights for your transaction
                templates
              </p>
            </div>
            <Select value={period} onValueChange={(v: any) => setPeriod(v)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">Last Week</SelectItem>
                <SelectItem value="month">Last Month</SelectItem>
                <SelectItem value="quarter">Last Quarter</SelectItem>
                <SelectItem value="year">Last Year</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Overall Stats */}
          <div className="grid md:grid-cols-4 gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Templates
                </CardTitle>
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {overallStats?.totalTemplates || 0}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {overallStats?.activeTemplates || 0} active
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Usage
                </CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {overallStats?.totalUsage || 0}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {overallStats?.avgUsagePerTemplate || 0} avg per template
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">
                  Time Saved
                </CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {costSavings?.hoursSaved || 0}h
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {costSavings?.minutesSaved || 0} minutes total
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">
                  Cost Savings
                </CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  CHF {costSavings?.costSavedCHF || 0}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {costSavings?.automatedTransactions || 0} automated txns
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Usage Trends */}
          <Card>
            <CardHeader>
              <CardTitle>Usage Trends</CardTitle>
              <CardDescription>
                Template usage in the selected period
              </CardDescription>
            </CardHeader>
            <CardContent>
              {usageTrends && usageTrends.trends.length > 0 ? (
                <div className="space-y-4">
                  {usageTrends.trends.slice(0, 10).map((trend: any) => (
                    <div
                      key={trend.templateId}
                      className="flex items-center justify-between"
                    >
                      <div className="flex-1">
                        <div className="font-medium">{trend.templateName}</div>
                        {trend.category && (
                          <div className="text-sm text-muted-foreground">
                            {trend.category}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="font-medium">
                            {trend.usageCount} uses
                          </div>
                          <div className="text-sm text-muted-foreground">
                            CHF {(trend.totalAmount / 100).toFixed(2)}
                          </div>
                        </div>
                        <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary"
                            style={{
                              width: `${Math.min((trend.usageCount / (usageTrends.trends[0]?.usageCount || 1)) * 100, 100)}%`,
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No usage data for the selected period
                </div>
              )}
            </CardContent>
          </Card>

          {/* Rankings and Category Breakdown */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Most Used Templates */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Award className="h-5 w-5" />
                  <CardTitle>Most Used Templates</CardTitle>
                </div>
                <CardDescription>
                  Top 5 templates by usage count
                </CardDescription>
              </CardHeader>
              <CardContent>
                {rankings && rankings.mostUsed.length > 0 ? (
                  <div className="space-y-3">
                    {rankings.mostUsed.map((template: any, index: number) => (
                      <div
                        key={template.id}
                        className="flex items-center gap-3"
                      >
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-bold">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">{template.name}</div>
                          {template.category && (
                            <div className="text-sm text-muted-foreground">
                              {template.category}
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="font-medium">
                            {template.usageCount}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            uses
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No templates yet
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Category Breakdown */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <PieChart className="h-5 w-5" />
                  <CardTitle>Category Breakdown</CardTitle>
                </div>
                <CardDescription>Template usage by category</CardDescription>
              </CardHeader>
              <CardContent>
                {categoryBreakdown && categoryBreakdown.breakdown.length > 0 ? (
                  <div className="space-y-3">
                    {categoryBreakdown.breakdown.map((item: any) => (
                      <div key={item.category} className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium">{item.category}</span>
                          <span className="text-muted-foreground">
                            {item.count} ({item.percentage}%)
                          </span>
                        </div>
                        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary"
                            style={{ width: `${item.percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No category data for the selected period
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
